import { useDispatch, useSelector } from 'react-redux';
import { Path } from 'react-hook-form';
import Select from 'react-select';
import { z } from 'zod';
import { ArrowLeftRight, ArrowUpDown, Plus, Ruler, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { useJobSetupModelForm } from '@/redux/post/usePostForm';
import { jobSetupModelSchema } from '@/redux/post/post.schema';
import ErrorMessage from '@/components/ErrorMessage';
import {
  IFlipDirection,
  IJobType,
  IUnits,
  IZZeroPosition,
  IXYDatumPosition,
  IToolSetter,
  IProbeForZHeightCommand,
  ISoftwareUsed,
  IMaterialUsed,
  GroupedEnum,
} from '@/redux/post/post.d'; // Adjust import path as needed
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { getGroupedOptionsFromEnumAsGroups } from '@/redux/post/post.utils';
import {
  setProjectEditorData,
  selectProjectEditorData,
} from '@/redux/post/post.slice';

function JobSetupDetails({ errors }: { errors: { [key: string]: string } }) {
  const dispatch = useDispatch();
  const projectEditorData = useSelector(selectProjectEditorData);
  const jobSetupModelForm = useJobSetupModelForm(projectEditorData);

  const handleSelectChange = (name: string, value: string | string[]): void => {
    jobSetupModelForm.setValue(
      name as Path<z.infer<typeof jobSetupModelSchema>>,
      value as unknown as never
    );
    dispatch(setProjectEditorData({ ...projectEditorData, [name]: value }));
  };

  const handleNumberChange = (name: string, value: number | string): void => {
    jobSetupModelForm.setValue(
      name as Path<z.infer<typeof jobSetupModelSchema>>,
      value as unknown as never
    );
    dispatch(setProjectEditorData({ ...projectEditorData, [name]: value }));
  };

  const getZZeroPositionOptions = (jobType: IJobType) => {
    if (jobType === IJobType.ROTARY) {
      return [IZZeroPosition.CYLINDER_SURFACE, IZZeroPosition.CYLINDER_AXIS];
    }
    return [IZZeroPosition.MATERIAL_SURFACE, IZZeroPosition.MACHINE_BED];
  };

  return (
    <div className="font-tertiary">
      <Card className="w-full max-w-6xl mx-auto">
        <CardContent className="p-4 sm:p-6">
          <Form {...jobSetupModelForm}>
            <form>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="softwareUsed"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Software Used
                        </FormLabel>
                        <FormControl>
                          <Select
                            isMulti
                            options={Object.values(ISoftwareUsed).map(
                              (option: ISoftwareUsed) => ({
                                label: option,
                                value: option,
                              })
                            )}
                            onChange={(selectedOptions) => {
                              const values = selectedOptions
                                ? (
                                    selectedOptions as Array<{
                                      label: string;
                                      value: ISoftwareUsed;
                                    }>
                                  ).map((opt) => opt.value)
                                : [];
                              field.onChange(values as ISoftwareUsed[]);
                              handleSelectChange(field.name, values);
                            }}
                            isSearchable
                            name={field.name}
                            value={
                              projectEditorData.softwareUsed &&
                              Array.isArray(projectEditorData.softwareUsed)
                                ? projectEditorData.softwareUsed.map(
                                    (software) => ({
                                      label: software,
                                      value: software,
                                    })
                                  )
                                : []
                            }
                          />
                        </FormControl>
                        <ErrorMessage error={errors.softwareUsed} />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={jobSetupModelForm.control}
                    name="materialUsed"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Material Used
                        </FormLabel>
                        <FormControl>
                          <Select
                            options={getGroupedOptionsFromEnumAsGroups(
                              IMaterialUsed as unknown as GroupedEnum
                            )}
                            onChange={(selectedOption) => {
                              if (selectedOption && selectedOption.label) {
                                field.onChange(selectedOption.label);
                                handleSelectChange(
                                  field.name,
                                  selectedOption.label
                                );
                              }
                            }}
                            isSearchable
                            name={field.name}
                            value={
                              projectEditorData.materialUsed
                                ? {
                                    label:
                                      projectEditorData.materialUsed as string,
                                    value:
                                      projectEditorData.materialUsed as string,
                                  }
                                : null
                            }
                          />
                        </FormControl>
                        <ErrorMessage error={errors.materialUsed} />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="jobType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Job Type
                        </FormLabel>
                        <FormControl>
                          <Select
                            options={Object.values(IJobType).map((option) => ({
                              label: option,
                              value: option,
                            }))}
                            onChange={(option) => {
                              if (option?.value) {
                                field.onChange(option.value);
                                if (option.value === IJobType.ROTARY) {
                                  const {
                                    jobSizeWidth,
                                    jobSizeHeight,
                                    jobSizeThickness,
                                    ...rest
                                  } = projectEditorData;
                                  dispatch(
                                    setProjectEditorData({
                                      ...rest,
                                      jobType: option.value,
                                      zZeroPosition:
                                        IZZeroPosition.CYLINDER_SURFACE,
                                      jobSizeDiameter:
                                        projectEditorData.jobSizeDiameter ||
                                        0.1,
                                      jobSizeLength:
                                        projectEditorData.jobSizeLength || 0.1,
                                    })
                                  );
                                } else {
                                  const {
                                    jobSizeDiameter,
                                    jobSizeLength,
                                    ...rest
                                  } = projectEditorData;
                                  const newFlipDirection =
                                    option.value === IJobType.DOUBLE_SIDED
                                      ? IFlipDirection.LEFT_TO_RIGHT
                                      : '';
                                  // Update form field for flipDirection
                                  jobSetupModelForm.setValue(
                                    'flipDirection' as Path<
                                      z.infer<typeof jobSetupModelSchema>
                                    >,
                                    newFlipDirection as never
                                  );
                                  dispatch(
                                    setProjectEditorData({
                                      ...rest,
                                      jobType: option.value,
                                      zZeroPosition:
                                        IZZeroPosition.MATERIAL_SURFACE,
                                      jobSizeWidth:
                                        projectEditorData.jobSizeWidth || 0.1,
                                      jobSizeHeight:
                                        projectEditorData.jobSizeHeight || 0.1,
                                      jobSizeThickness:
                                        projectEditorData.jobSizeThickness ||
                                        0.1,
                                      flipDirection: newFlipDirection,
                                    })
                                  );
                                }
                              }
                            }}
                            isSearchable
                            name={field.name}
                            value={
                              projectEditorData.jobType
                                ? {
                                    label: projectEditorData.jobType,
                                    value: projectEditorData.jobType,
                                  }
                                : null
                            }
                          />
                        </FormControl>
                        <ErrorMessage error={errors.jobSetupModel} />
                      </FormItem>
                    )}
                  />

                  {projectEditorData.jobType === IJobType.DOUBLE_SIDED && (
                    <FormField
                      control={jobSetupModelForm.control}
                      name="flipDirection"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium mb-2">
                            Flip Direction
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={(value) => {
                                field.onChange(value);
                                handleSelectChange(field.name, value);
                              }}
                              value={
                                field.value !== undefined
                                  ? field.value
                                  : projectEditorData.flipDirection
                              }
                              className="flex gap-4"
                            >
                              <div className="flex items-center space-x-2 w-full border border-[#D6D6D6] h-[40px] p-2 rounded-sm cursor-pointer">
                                <RadioGroupItem
                                  value={IFlipDirection.LEFT_TO_RIGHT}
                                  id={IFlipDirection.LEFT_TO_RIGHT}
                                />
                                <label
                                  htmlFor={IFlipDirection.LEFT_TO_RIGHT}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer flex items-center gap-2"
                                >
                                  <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
                                  {IFlipDirection.LEFT_TO_RIGHT}
                                </label>
                              </div>
                              <div className="flex items-center space-x-2 w-full border border-[#D6D6D6] h-[40px] p-2 rounded-sm cursor-pointer">
                                <RadioGroupItem
                                  value={IFlipDirection.FRONT_TO_BACK}
                                  id={IFlipDirection.FRONT_TO_BACK}
                                />
                                <label
                                  htmlFor={IFlipDirection.FRONT_TO_BACK}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer flex items-center gap-2"
                                >
                                  <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                                  {IFlipDirection.FRONT_TO_BACK}
                                </label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <ErrorMessage error={errors.flipDirection} />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={jobSetupModelForm.control}
                    name="units"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Job Units
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) => {
                              field.onChange(value);
                              handleSelectChange(field.name, value);
                            }}
                            value={
                              field.value !== undefined
                                ? field.value
                                : projectEditorData.units
                            }
                            className="flex gap-4"
                          >
                            {Object.values(IUnits).map((option) => (
                              <div
                                key={option}
                                className="flex items-center space-x-2 w-full border border-[#D6D6D6] h-[40px] p-2 rounded-sm cursor-pointer"
                              >
                                <RadioGroupItem value={option} id={option} />
                                <label
                                  htmlFor={option}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
                                >
                                  {option}
                                </label>
                              </div>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <ErrorMessage error={errors.units} />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Ruler className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-base font-semibold">Job Size</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {projectEditorData.jobType === IJobType.ROTARY ? (
                      <>
                        <div className="flex flex-col gap-2">
                          <FormField
                            control={jobSetupModelForm.control}
                            name="jobSizeDiameter"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium mb-2">
                                  Diameter in {projectEditorData.units}
                                </FormLabel>
                                <FormControl>
                                  <input
                                    type="number"
                                    value={
                                      field.value !== undefined
                                        ? field.value
                                        : projectEditorData.jobSizeDiameter
                                    }
                                    onChange={({ target: { value } }) => {
                                      field.onChange(value);
                                      handleNumberChange(field.name, value);
                                    }}
                                    step="0.25"
                                    onBlur={(e) => {
                                      const value = parseFloat(e.target.value);
                                      field.onChange(value);
                                      handleNumberChange(field.name, value);
                                    }}
                                    className="w-full p-2 border rounded"
                                  />
                                </FormControl>
                                <ErrorMessage error={errors.jobSizeDiameter} />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="flex flex-col gap-2">
                          <FormField
                            control={jobSetupModelForm.control}
                            name="jobSizeLength"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium mb-2">
                                  Length in {projectEditorData.units}
                                </FormLabel>
                                <FormControl>
                                  <input
                                    type="number"
                                    value={
                                      field.value !== undefined
                                        ? field.value
                                        : projectEditorData.jobSizeLength
                                    }
                                    step="0.25"
                                    onChange={({ target: { value } }) => {
                                      field.onChange(value);
                                      handleNumberChange(field.name, value);
                                    }}
                                    onBlur={(e) => {
                                      const value = parseFloat(e.target.value);
                                      field.onChange(value);
                                      handleNumberChange(field.name, value);
                                    }}
                                    className="w-full p-2 border rounded"
                                  />
                                </FormControl>
                                <ErrorMessage error={errors.jobSizeLength} />
                              </FormItem>
                            )}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-col gap-2">
                          <FormField
                            control={jobSetupModelForm.control}
                            name="jobSizeWidth"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium mb-2">
                                  (X) Width in {projectEditorData.units}
                                </FormLabel>
                                <FormControl>
                                  <input
                                    type="number"
                                    value={
                                      field.value !== undefined
                                        ? field.value
                                        : projectEditorData.jobSizeWidth
                                    }
                                    step="0.25"
                                    onChange={({ target: { value } }) => {
                                      field.onChange(value);
                                      handleNumberChange(field.name, value);
                                    }}
                                    onBlur={(e) => {
                                      const value = parseFloat(e.target.value);
                                      field.onChange(value);
                                      handleNumberChange(field.name, value);
                                    }}
                                    className="w-full p-2 border rounded"
                                  />
                                </FormControl>
                                <ErrorMessage error={errors.jobSizeWidth} />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="flex flex-col gap-2">
                          <FormField
                            control={jobSetupModelForm.control}
                            name="jobSizeHeight"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium mb-2">
                                  (Y) Height in {projectEditorData.units}
                                </FormLabel>
                                <FormControl>
                                  <input
                                    type="number"
                                    value={
                                      field.value !== undefined
                                        ? field.value
                                        : projectEditorData.jobSizeHeight
                                    }
                                    step="0.25"
                                    onChange={({ target: { value } }) => {
                                      field.onChange(value);
                                      handleNumberChange(field.name, value);
                                    }}
                                    onBlur={(e) => {
                                      const value = parseFloat(e.target.value);
                                      field.onChange(value);
                                      handleNumberChange(field.name, value);
                                    }}
                                    className="w-full p-2 border rounded"
                                  />
                                </FormControl>
                                <ErrorMessage error={errors.jobSizeHeight} />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="flex flex-col gap-2">
                          <FormField
                            control={jobSetupModelForm.control}
                            name="jobSizeThickness"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium mb-2">
                                  (Z) Thickness in {projectEditorData.units}
                                </FormLabel>
                                <FormControl>
                                  <input
                                    type="number"
                                    value={
                                      field.value !== undefined
                                        ? field.value
                                        : projectEditorData.jobSizeThickness
                                    }
                                    step="0.25"
                                    onChange={({ target: { value } }) => {
                                      field.onChange(value);
                                      handleNumberChange(field.name, value);
                                    }}
                                    onBlur={(e) => {
                                      const value = parseFloat(e.target.value);
                                      field.onChange(value);
                                      handleNumberChange(field.name, value);
                                    }}
                                    className="w-full p-2 border rounded"
                                  />
                                </FormControl>
                                <ErrorMessage error={errors.jobSizeThickness} />
                              </FormItem>
                            )}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="zZeroPosition"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Z Zero Position
                        </FormLabel>
                        <FormControl>
                          <Select
                            options={getZZeroPositionOptions(
                              projectEditorData.jobType
                            ).map((option) => ({
                              label: option,
                              value: option,
                            }))}
                            onChange={(option) => {
                              if (option?.value) {
                                field.onChange(option.value);
                                handleSelectChange(field.name, option.value);
                              }
                            }}
                            isSearchable
                            name={field.name}
                            value={
                              projectEditorData.zZeroPosition
                                ? {
                                    label: projectEditorData.zZeroPosition,
                                    value: projectEditorData.zZeroPosition,
                                  }
                                : null
                            }
                          />
                        </FormControl>
                        <ErrorMessage error={errors.zZeroPosition} />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={jobSetupModelForm.control}
                    name="xyDatumPosition"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          XY Datum/Origin Position
                        </FormLabel>
                        <FormControl>
                          <Select
                            options={Object.values(IXYDatumPosition).map(
                              (option) => ({
                                label: option,
                                value: option,
                              })
                            )}
                            onChange={(option) => {
                              if (option?.value) {
                                field.onChange(option.value);
                                handleSelectChange(field.name, option.value);
                              }
                            }}
                            isSearchable
                            name={field.name}
                            value={
                              projectEditorData.xyDatumPosition
                                ? {
                                    label: projectEditorData.xyDatumPosition,
                                    value: projectEditorData.xyDatumPosition,
                                  }
                                : null
                            }
                          />
                        </FormControl>
                        <ErrorMessage error={errors.xyDatumPosition} />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="toolSetter"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Tool Setter
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) => {
                              field.onChange(value);
                              handleSelectChange(field.name, value);
                            }}
                            value={
                              field.value !== undefined
                                ? field.value
                                : projectEditorData.toolSetter
                            }
                            className="flex gap-4"
                          >
                            {Object.values(IToolSetter).map((option) => (
                              <div
                                key={option}
                                className="flex items-center space-x-2 w-full border border-[#D6D6D6] h-[40px] p-2 rounded-sm cursor-pointer"
                              >
                                <RadioGroupItem value={option} id={option} />
                                <label
                                  htmlFor={option}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
                                >
                                  {option}
                                </label>
                              </div>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <ErrorMessage error={errors.toolSetter} />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={jobSetupModelForm.control}
                    name="probeForZHeightCommand"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Automatic Tool Changer (ATC)
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) => {
                              field.onChange(value);
                              handleSelectChange(field.name, value);
                            }}
                            value={
                              field.value !== undefined
                                ? field.value
                                : projectEditorData.probeForZHeightCommand
                            }
                            className="flex gap-4"
                          >
                            {Object.values(IProbeForZHeightCommand).map(
                              (option) => (
                                <div
                                  key={option}
                                  className="flex items-center space-x-2 w-full border border-[#D6D6D6] h-[40px] p-2 rounded-sm cursor-pointer"
                                >
                                  <RadioGroupItem value={option} id={option} />
                                  <label
                                    htmlFor={option}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
                                  >
                                    {option}
                                  </label>
                                </div>
                              )
                            )}
                          </RadioGroup>
                        </FormControl>
                        <ErrorMessage error={errors.probeForZHeightCommand} />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="isDimensionalMaterial"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Big Box Store Lumber
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) => {
                              const isYes = value === 'Yes';
                              field.onChange(isYes);
                              dispatch(
                                setProjectEditorData({
                                  ...projectEditorData,
                                  isDimensionalMaterial: isYes,
                                  dimensionalMaterialLinks: isYes
                                    ? projectEditorData.dimensionalMaterialLinks
                                    : [],
                                })
                              );
                              jobSetupModelForm.setValue(
                                'isDimensionalMaterial' as Path<
                                  z.infer<typeof jobSetupModelSchema>
                                >,
                                isYes as never
                              );
                            }}
                            value={
                              projectEditorData.isDimensionalMaterial
                                ? 'Yes'
                                : 'No'
                            }
                            className="flex gap-4"
                          >
                            {['Yes', 'No'].map((option) => (
                              <div
                                key={option}
                                className="flex items-center space-x-2 w-full border border-[#D6D6D6] h-[40px] p-2 rounded-sm cursor-pointer"
                              >
                                <RadioGroupItem
                                  value={option}
                                  id={`dimensional-material-${option}`}
                                />
                                <label
                                  htmlFor={`dimensional-material-${option}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
                                >
                                  {option}
                                </label>
                              </div>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <ErrorMessage error={errors.isDimensionalMaterial} />
                      </FormItem>
                    )}
                  />
                </div>

                {projectEditorData.isDimensionalMaterial && (
                  <FormItem>
                    <FormLabel className="text-sm font-medium mb-2">
                      Material Links
                    </FormLabel>
                    <div className="space-y-3">
                      {projectEditorData.dimensionalMaterialLinks.map(
                        (url, index) => (
                          <div key={index} className="flex gap-3 items-center">
                            <input
                              type="url"
                              placeholder="https://example.com/material"
                              value={url}
                              onChange={(e) => {
                                const updated =
                                  projectEditorData.dimensionalMaterialLinks.map(
                                    (u, i) => (i === index ? e.target.value : u)
                                  );
                                dispatch(
                                  setProjectEditorData({
                                    ...projectEditorData,
                                    dimensionalMaterialLinks: updated,
                                  })
                                );
                                jobSetupModelForm.setValue(
                                  'dimensionalMaterialLinks' as Path<
                                    z.infer<typeof jobSetupModelSchema>
                                  >,
                                  updated as never
                                );
                              }}
                              className="flex-1 p-2 border rounded text-sm placeholder:text-sm h-[40px]"
                            />
                            <button
                              type="button"
                              aria-label="Remove link"
                              onClick={() => {
                                const updated =
                                  projectEditorData.dimensionalMaterialLinks.filter(
                                    (_, i) => i !== index
                                  );
                                dispatch(
                                  setProjectEditorData({
                                    ...projectEditorData,
                                    dimensionalMaterialLinks: updated,
                                  })
                                );
                                jobSetupModelForm.setValue(
                                  'dimensionalMaterialLinks' as Path<
                                    z.infer<typeof jobSetupModelSchema>
                                  >,
                                  updated as never
                                );
                              }}
                              className="p-2 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const updated = [
                            ...projectEditorData.dimensionalMaterialLinks,
                            '',
                          ];
                          dispatch(
                            setProjectEditorData({
                              ...projectEditorData,
                              dimensionalMaterialLinks: updated,
                            })
                          );
                          jobSetupModelForm.setValue(
                            'dimensionalMaterialLinks' as Path<
                              z.infer<typeof jobSetupModelSchema>
                            >,
                            updated as never
                          );
                        }}
                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <Plus className="h-4 w-4" />
                        Add Link
                      </button>
                      <ErrorMessage error={errors.dimensionalMaterialLinks} />
                    </div>
                  </FormItem>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}

export default JobSetupDetails;
